import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  RefreshCw,
  Loader2,
  CheckCircle2,
  XCircle,
  Star,
  CircleDot,
  Euro,
  TrendingUp,
  Search,
  Edit3,
  Download,
  Trash2,
  Copy,
  UserCircle,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Display order + french label + isFullWidth for the extracted form fields
const LEAD_FIELD_ORDER = [
  ['full_name', 'Nom complet', false],
  ['first_name', 'Prénom', false],
  ['last_name', 'Nom', false],
  ['email', 'Email', false],
  ['phone', 'Téléphone', false],
  ['company', 'Société', false],
  ['address', 'Adresse', true],
  ['postal_code', 'Code postal', false],
  ['city', 'Ville', false],
  ['surface', 'Surface', false],
  ['subject_request', 'Projet', true],
  ['message', 'Message', true],
];

const LOSS_REASONS = [
  { value: 'prix', label: 'Prix trop élevé' },
  { value: 'delai', label: 'Délai trop long' },
  { value: 'concurrent', label: 'Choisi un concurrent' },
  { value: 'ne_repond_plus', label: 'Ne répond plus' },
  { value: 'hors_zone', label: 'Hors zone d\'intervention' },
  { value: 'pas_serieux', label: 'Pas sérieux / spam' },
  { value: 'autre', label: 'Autre' },
];

const STATUS_OPTIONS = [
  { value: 'new', label: 'Nouveau', icon: CircleDot, color: 'bg-neutral-100 text-neutral-700' },
  { value: 'qualified', label: 'Qualifié', icon: Star, color: 'bg-blue-100 text-blue-800' },
  { value: 'won', label: 'Gagné', icon: CheckCircle2, color: 'bg-green-100 text-green-800' },
  { value: 'lost', label: 'Perdu', icon: XCircle, color: 'bg-red-100 text-red-800' },
];

const formatEUR = (n) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(n || 0);
const formatDate = (s) => s ? new Date(s).toLocaleDateString('fr-FR') : '—';
const formatDateTime = (s) => s ? new Date(s).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

// ─────────────────────────────────────────────────────────────
// localStorage cache for the leads list
// Persist last fetched data per filter combo so the page renders
// instantly on next visit (stale-while-revalidate pattern).
// TTL = 10 min max to avoid stale display.
// ─────────────────────────────────────────────────────────────
const CACHE_KEY = 'aegis:conversions:cache:v1';
const CACHE_TTL_MS = 10 * 60 * 1000;
const cacheKey = (selectedClient, statusFilter, period) =>
  `${selectedClient}|${statusFilter}|${period}`;

const loadCache = (key) => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const all = JSON.parse(raw);
    const entry = all[key];
    if (!entry) return null;
    if (Date.now() - entry.t > CACHE_TTL_MS) return null;
    return entry;
  } catch {
    return null;
  }
};

const saveCache = (key, payload) => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    const all = raw ? JSON.parse(raw) : {};
    all[key] = { t: Date.now(), ...payload };
    // Keep only the 8 most recent combos to avoid unbounded growth
    const keys = Object.keys(all).sort((a, b) => all[b].t - all[a].t).slice(0, 8);
    const trimmed = {};
    keys.forEach(k => { trimmed[k] = all[k]; });
    localStorage.setItem(CACHE_KEY, JSON.stringify(trimmed));
  } catch {
    // quota exceeded or unavailable — silent fallback
  }
};

// Extract UTM parameters from a free-text body (looking for utm_source=..., utm_medium=..., gclid=...).
// Returns { utm_source, utm_medium, utm_campaign, utm_term, utm_content, gclid } or empty obj.
const extractUTMs = (text) => {
  if (!text) return {};
  const out = {};
  const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'fbclid', 'msclkid'];
  for (const k of keys) {
    const m = new RegExp(`${k}\\s*[=:]\\s*([\\w\\-.+%]+)`, 'i').exec(text);
    if (m) out[k] = decodeURIComponent(m[1]);
  }
  return out;
};

export default function Conversions() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [period, setPeriod] = useState('6_months');
  const [data, setData] = useState({ leads: [], counts: {}, total_revenue: 0, conversion_rate: 0 });
  const [roiData, setRoiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [editingLead, setEditingLead] = useState(null);

  const [start, end] = useMemo(() => {
    const today = new Date();
    const fmt = (d) => d.toISOString().slice(0, 10);
    const sub = (m) => { const d = new Date(today); d.setMonth(d.getMonth() - m); return d; };
    if (period === '1_month') return [fmt(sub(1)), fmt(today)];
    if (period === '3_months') return [fmt(sub(3)), fmt(today)];
    if (period === '6_months') return [fmt(sub(6)), fmt(today)];
    if (period === '12_months') return [fmt(sub(12)), fmt(today)];
    if (period === 'ytd') return [`${today.getFullYear()}-01-01`, fmt(today)];
    return [fmt(sub(6)), fmt(today)];
  }, [period]);

  // Load clients once
  useEffect(() => {
    axios.get(`${API}/clients`, { withCredentials: true })
      .then(({ data }) => setClients(data || []))
      .catch(() => toast.error('Erreur lors du chargement des clients'));
  }, []);

  const fetchData = useCallback(async () => {
    const ck = cacheKey(selectedClient, statusFilter, period);
    // Hydrate from cache instantly (stale-while-revalidate)
    const cached = loadCache(ck);
    if (cached) {
      setData(cached.data || { leads: [], counts: {}, total_revenue: 0, conversion_rate: 0 });
      setRoiData(cached.roi || null);
      setRefreshing(true); // show subtle indicator while we fetch fresh
      setLoading(false);
    } else {
      setLoading(true);
    }
    try {
      const params = new URLSearchParams({ start, end, limit: 1000 });
      if (selectedClient !== 'all') params.append('client_id', selectedClient);
      if (statusFilter !== 'all') params.append('status', statusFilter);

      const [leadsRes, roiRes] = await Promise.all([
        axios.get(`${API}/conversions/leads?${params}`, { withCredentials: true }),
        axios.get(`${API}/conversions/roi?${new URLSearchParams({ start, end, ...(selectedClient !== 'all' ? { client_id: selectedClient } : {}) })}`, { withCredentials: true }),
      ]);
      setData(leadsRes.data);
      setRoiData(roiRes.data);
      saveCache(ck, { data: leadsRes.data, roi: roiRes.data });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedClient, statusFilter, start, end, period]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSync = async (forceRefresh = false) => {
    setSyncing(true);
    try {
      const qs = new URLSearchParams({ months: '12' });
      if (selectedClient !== 'all') qs.set('client_id', selectedClient);
      if (forceRefresh) qs.set('force_refresh_parsing', 'true');
      const { data: res } = await axios.post(`${API}/conversions/sync?${qs}`, {}, { withCredentials: true });
      const errCount = res.error_count || 0;
      if (forceRefresh) {
        const msg = `Re-parsing : ${res.refreshed || 0} mis à jour, ${res.inserted} nouveau(x)${errCount ? ` · ${errCount} erreur(s)` : ''}`;
        if (errCount) toast.warning(msg, { description: (res.errors || []).join(' / ') });
        else toast.success(msg);
      } else {
        const msg = `Synchro Gmail : ${res.inserted} nouveau(x) lead(s) importé(s)${errCount ? ` · ${errCount} erreur(s)` : ''}`;
        if (errCount) toast.warning(msg, { description: (res.errors || []).join(' / ') });
        else toast.success(msg);
      }
      try { localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
      fetchData();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (e.message || 'Erreur de synchronisation');
      toast.error(msg);
    } finally {
      setSyncing(false);
    }
  };

  const handleDeleteLead = async (lead) => {
    if (!lead?.id) return;
    const desc = lead.extracted_fields?.full_name || lead.subject || lead.from_email || 'ce lead';
    if (!window.confirm(`Supprimer définitivement ${desc} ?\n\nCette action est irréversible.`)) return;
    try {
      await axios.delete(`${API}/conversions/leads/${lead.id}`, { withCredentials: true });
      toast.success('Lead supprimé');
      try { localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur suppression');
    }
  };

  const updateLead = async (lead, patch) => {
    try {
      const { data: updated } = await axios.put(`${API}/conversions/leads/${lead.id}`, patch, { withCredentials: true });
      setData((d) => ({
        ...d,
        leads: d.leads.map(l => l.id === updated.id ? updated : l),
      }));
      // Refresh aggregates after a status change
      if ('status' in patch || 'sale_value' in patch) fetchData();
      return updated;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de mise à jour');
      throw e;
    }
  };

  const exportCsv = () => {
    if (!filteredLeads.length) return;
    const cols = ['Date', 'Client', 'De', 'Sujet', 'Source', 'Statut', 'Valeur (€)', 'Notes'];
    const rows = filteredLeads.map(l => [
      formatDate(l.received_at),
      l.client_name || '',
      l.from_email || '',
      l.subject || '',
      l.source || '',
      STATUS_OPTIONS.find(s => s.value === l.status)?.label || l.status,
      l.sale_value || 0,
      (l.notes || '').replace(/\n/g, ' '),
    ]);
    const csv = '\uFEFF' + [cols.join(';'), ...rows.map(r => r.map(c => {
      const s = String(c ?? '');
      return /[;\n"]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(';'))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `conversions_${selectedClient}_${start}_${end}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false);

  const filteredLeads = data.leads.filter(l => {
    if (showDuplicatesOnly && !l.is_duplicate) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (l.subject || '').toLowerCase().includes(s)
      || (l.from_email || '').toLowerCase().includes(s)
      || (l.client_name || '').toLowerCase().includes(s);
  });

  const duplicateCount = data.leads.filter(l => l.is_duplicate).length;

  const handleScanDuplicates = async (reset = false) => {
    try {
      const { data: res } = await axios.post(
        `${API}/conversions/scan-duplicates${reset ? '?reset=true' : ''}`,
        {},
        { withCredentials: true },
      );
      toast.success(
        `Scan terminé : ${res.flagged_as_duplicate} doublon(s) sur ${res.scanned} lead(s)` +
        (res.signature_backfilled ? ` · ${res.signature_backfilled} signature(s) calculée(s)` : ''),
      );
      try { localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur scan');
    }
  };

  const handleDismissDuplicate = async (lead) => {
    try {
      await axios.post(`${API}/conversions/leads/${lead.id}/dismiss-duplicate`, {}, { withCredentials: true });
      toast.success('Marqué comme non-doublon');
      try { localStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <Layout>
      <div className="space-y-5" data-testid="conversions-page">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="font-heading font-bold text-2xl sm:text-3xl text-neutral-900">Conversions & ventes</h1>
            <p className="text-neutral-500 mt-1">
              Marquez chaque lead comme gagné/perdu pour calculer le <strong>ROI réel</strong> à partir des ventes effectives.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {refreshing && (
              <span
                className="inline-flex items-center gap-1 text-xs text-neutral-500"
                data-testid="conv-refreshing"
                title="Mise à jour des leads en arrière-plan"
              >
                <Loader2 className="w-3 h-3 animate-spin" />
                Actualisation…
              </span>
            )}
            <Button onClick={() => handleSync(false)} disabled={syncing} variant="outline" data-testid="conv-sync-btn">
              <RefreshCw className={`w-4 h-4 mr-1 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Synchro…' : 'Synchroniser Gmail'}
            </Button>
            <Button
              onClick={() => {
                if (window.confirm("Re-parser les emails Gmail :\n\n• les nouveaux leads seront importés normalement\n• les leads existants verront leur contenu / source / UTMs RAFRAÎCHIS depuis Gmail\n• vos statuts, valeurs de vente, notes et raisons de perte sont PRÉSERVÉS\n\nUtile après une mise à jour du parser. Continuer ?")) {
                  handleSync(true);
                }
              }}
              disabled={syncing}
              variant="outline"
              size="sm"
              className="text-xs"
              data-testid="conv-resync-btn"
              title="Re-fetch les emails Gmail et nettoie le HTML existant — préserve vos statuts / ventes / notes"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${syncing ? 'animate-spin' : ''}`} />
              Re-parser
            </Button>
            {user?.role === 'admin' && (
              <Button
                onClick={() => handleScanDuplicates(false)}
                variant="outline"
                size="sm"
                className="text-xs"
                data-testid="scan-dup-btn"
                title="Détecter les leads similaires par contenu (email/téléphone/nom + ±48h, par client)"
              >
                <Copy className="w-3.5 h-3.5 mr-1" />
                Scan doublons
              </Button>
            )}
            {user?.role === 'admin' && (
              <Button
                onClick={() => {
                  if (window.confirm('Réinitialiser tous les flags doublon (sauf ceux marqués manuellement comme non-doublon) puis relancer la détection ?')) {
                    handleScanDuplicates(true);
                  }
                }}
                variant="outline"
                size="sm"
                className="text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
                data-testid="scan-dup-reset-btn"
                title="Efface d'abord tous les flags doublon puis relance la détection avec la nouvelle logique (utile après un mauvais scan)"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1" />
                Reset + Rescan
              </Button>
            )}
            <Button onClick={exportCsv} variant="outline" disabled={!filteredLeads.length} data-testid="conv-export-btn">
              <Download className="w-4 h-4 mr-1" /> CSV
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 bg-white border border-neutral-200 rounded-lg p-3">
          <Select value={selectedClient} onValueChange={setSelectedClient}>
            <SelectTrigger className="w-56" data-testid="conv-client-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les clients</SelectItem>
              {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40" data-testid="conv-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="1_month">Dernier mois</SelectItem>
              <SelectItem value="3_months">3 derniers mois</SelectItem>
              <SelectItem value="6_months">6 derniers mois</SelectItem>
              <SelectItem value="12_months">12 derniers mois</SelectItem>
              <SelectItem value="ytd">Année en cours</SelectItem>
            </SelectContent>
          </Select>

          {duplicateCount > 0 && (
            <Button
              variant={showDuplicatesOnly ? 'default' : 'outline'}
              size="sm"
              onClick={() => setShowDuplicatesOnly(v => !v)}
              className={`h-9 text-xs ${showDuplicatesOnly ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'text-amber-700 border-amber-300 hover:bg-amber-50'}`}
              data-testid="dup-filter-toggle"
            >
              <Copy className="w-3.5 h-3.5 mr-1" />
              {showDuplicatesOnly ? `Doublons (${duplicateCount}) ✓` : `Doublons (${duplicateCount})`}
            </Button>
          )}

          <div className="relative ml-auto">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher sujet, email…"
              className="pl-7 w-72 h-9"
              data-testid="conv-search-input"
            />
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <KpiCard label="Total leads" value={data.total} sub={`${filteredLeads.length} affichés`} />
          <KpiCard label="Gagnés" value={data.counts?.won || 0} highlight="green" />
          <KpiCard label="Qualifiés" value={data.counts?.qualified || 0} highlight="blue" />
          <KpiCard label="Taux conv." value={`${(data.conversion_rate * 100).toFixed(1)}%`} icon={TrendingUp} />
          <KpiCard label="Revenus (gagnés)" value={formatEUR(data.total_revenue)} highlight="orange" icon={Euro} />
        </div>

        {/* Real ROI summary */}
        {roiData && roiData.totals?.cost > 0 && (
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-lg p-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide font-mono text-green-700">ROI Réel — basé sur les ventes</p>
                <p className="font-heading font-bold text-3xl text-neutral-900 mt-1">
                  {roiData.totals.roi_percent !== null ? `${roiData.totals.roi_percent.toFixed(0)}%` : '—'}
                </p>
                <p className="text-xs text-neutral-500 mt-0.5">
                  Revenus {formatEUR(roiData.totals.revenue)} − Coût marketing {formatEUR(roiData.totals.cost)} = <strong>{formatEUR(roiData.totals.net_roi)}</strong>
                </p>
              </div>
              <div className="text-right text-sm">
                <p className="text-green-800 font-medium">{roiData.totals.wins} vente(s) gagnée(s)</p>
                <p className="text-neutral-500 text-xs">sur {roiData.months} mois</p>
              </div>
            </div>
          </div>
        )}

        {/* Leads table */}
        {loading ? (
          <div className="flex items-center justify-center p-12"><Loader2 className="w-6 h-6 animate-spin text-neutral-400" /></div>
        ) : filteredLeads.length === 0 ? (
          <div className="bg-white border border-neutral-200 rounded-lg p-8 text-center">
            <p className="text-neutral-500 mb-3">Aucun lead trouvé pour cette période/filtre.</p>
            <Button onClick={() => handleSync(false)} disabled={syncing}>
              <RefreshCw className="w-4 h-4 mr-1" /> Synchroniser depuis Gmail
            </Button>
          </div>
        ) : (
          <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-neutral-600">Date</th>
                  <th className="text-left px-3 py-2 font-medium text-neutral-600">Client</th>
                  <th className="text-left px-3 py-2 font-medium text-neutral-600">Lead</th>
                  <th className="text-left px-3 py-2 font-medium text-neutral-600">Source</th>
                  <th className="text-left px-3 py-2 font-medium text-neutral-600">Statut</th>
                  <th className="text-right px-3 py-2 font-medium text-neutral-600">Valeur</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((l) => (
                  <LeadRow
                    key={l.id}
                    lead={l}
                    clients={clients}
                    onUpdate={(patch) => updateLead(l, patch)}
                    onEdit={() => setEditingLead(l)}
                    onDelete={() => handleDeleteLead(l)}
                    onDismissDuplicate={() => handleDismissDuplicate(l)}
                    canDelete={user?.role === 'admin'}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Edit dialog */}
        <EditLeadDialog
          lead={editingLead}
          clients={clients}
          open={!!editingLead}
          onOpenChange={(o) => !o && setEditingLead(null)}
          onSave={async (patch) => {
            await updateLead(editingLead, patch);
            setEditingLead(null);
          }}
        />
      </div>
    </Layout>
  );
}

function KpiCard({ label, value, sub, highlight, icon: Icon }) {
  const colors = {
    green: 'bg-green-50 border-green-200 text-green-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    orange: 'bg-orange-50 border-orange-200 text-orange-800',
  };
  const cls = colors[highlight] || 'bg-white border-neutral-200 text-neutral-900';
  return (
    <div className={`border rounded-lg p-3 ${cls}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs uppercase tracking-wide font-mono opacity-70">{label}</p>
        {Icon && <Icon className="w-3.5 h-3.5 opacity-50" />}
      </div>
      <p className="font-heading font-bold text-xl mt-1">{value}</p>
      {sub && <p className="text-xs opacity-60 mt-0.5">{sub}</p>}
    </div>
  );
}

function LeadRow({ lead, clients, onUpdate, onEdit, onDelete, onDismissDuplicate, canDelete }) {
  const status = STATUS_OPTIONS.find(s => s.value === lead.status) || STATUS_OPTIONS[0];
  const Icon = status.icon;
  return (
    <tr className="border-b border-neutral-50 hover:bg-neutral-50" data-testid={`lead-row-${lead.id}`}>
      <td className="px-3 py-2 text-xs text-neutral-500 whitespace-nowrap">{formatDate(lead.received_at)}</td>
      <td className="px-3 py-2">
        {lead.client_name ? (
          <span className="font-medium text-neutral-900">{lead.client_name}</span>
        ) : (
          <Select
            value=""
            onValueChange={(v) => onUpdate({ client_id: v })}
          >
            <SelectTrigger className="h-7 text-xs w-44 border-amber-300 bg-amber-50">
              <SelectValue placeholder="Non assigné" />
            </SelectTrigger>
            <SelectContent>
              {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
      </td>
      <td className="px-3 py-2 max-w-md">
        <button
          type="button"
          className="text-left w-full group"
          onClick={onEdit}
          data-testid={`lead-open-${lead.id}`}
          title="Voir le détail du formulaire"
        >
          {lead.extracted_fields?.full_name || lead.extracted_fields?.email ? (
            <>
              <div className="text-neutral-900 truncate font-medium group-hover:text-[#002FA7] flex items-center gap-1.5">
                {lead.extracted_fields.full_name || '(sans nom)'}
                {lead.is_duplicate && (
                  <Badge className="bg-amber-100 text-amber-800 border-amber-200 border text-[9px] px-1 py-0" title="Doublon présumé d'un lead antérieur" data-testid={`dup-badge-${lead.id}`}>
                    <Copy className="w-2.5 h-2.5 mr-0.5" /> Doublon ?
                  </Badge>
                )}
              </div>
              <div className="text-xs text-neutral-500 truncate flex items-center gap-2">
                {lead.extracted_fields.email && <span className="font-mono">{lead.extracted_fields.email}</span>}
                {lead.extracted_fields.phone && <span className="text-neutral-400">· {lead.extracted_fields.phone}</span>}
              </div>
            </>
          ) : (
            <>
              <div className="text-neutral-900 truncate group-hover:text-[#002FA7] flex items-center gap-1.5">
                {lead.subject || '(sans sujet)'}
                {lead.is_duplicate && (
                  <Badge className="bg-amber-100 text-amber-800 border-amber-200 border text-[9px] px-1 py-0" title="Doublon présumé">
                    <Copy className="w-2.5 h-2.5 mr-0.5" /> Doublon ?
                  </Badge>
                )}
              </div>
              <div className="text-xs text-neutral-400 truncate">{lead.from_email}</div>
            </>
          )}
        </button>
      </td>
      <td className="px-3 py-2">
        <Badge variant="outline" className="text-[10px] font-normal">{lead.source}</Badge>
      </td>
      <td className="px-3 py-2">
        <Select value={lead.status} onValueChange={(v) => onUpdate({ status: v })}>
          <SelectTrigger className="h-7 text-xs w-32" data-testid={`lead-status-${lead.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STATUS_OPTIONS.map(s => (
              <SelectItem key={s.value} value={s.value}>
                <div className="flex items-center gap-1.5">
                  <s.icon className="w-3 h-3" /> {s.label}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="px-3 py-2 text-right">
        {lead.status === 'won' ? (
          <Input
            type="number"
            min={0}
            step={50}
            defaultValue={lead.sale_value || ''}
            placeholder="0"
            className="w-24 h-7 text-right text-sm"
            onBlur={(e) => {
              const v = parseFloat(e.target.value) || 0;
              if (v !== lead.sale_value) onUpdate({ sale_value: v });
            }}
            data-testid={`lead-sale-value-${lead.id}`}
          />
        ) : (
          <span className="text-neutral-300 text-xs">—</span>
        )}
      </td>
      <td className="px-3 py-2 text-right">
        {lead.is_duplicate && (
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); onDismissDuplicate?.(); }}
            className="h-7 w-7 text-amber-600 hover:text-amber-800 hover:bg-amber-50"
            title="Marquer comme non-doublon"
            data-testid={`dismiss-dup-${lead.id}`}
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
          </Button>
        )}
        <Button variant="ghost" size="icon" onClick={onEdit} className="h-7 w-7" data-testid={`lead-edit-${lead.id}`}>
          <Edit3 className="w-3.5 h-3.5" />
        </Button>
        {canDelete && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onDelete}
            className="h-7 w-7 text-rose-500 hover:text-rose-700 hover:bg-rose-50"
            data-testid={`lead-delete-${lead.id}`}
            title="Supprimer ce lead"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </td>
    </tr>
  );
}

function EditLeadDialog({ lead, clients, open, onOpenChange, onSave }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (lead) setForm({
      status: lead.status,
      sale_value: lead.sale_value || 0,
      notes: lead.notes || '',
      client_id: lead.client_id || '',
      loss_reason: lead.loss_reason || '',
    });
  }, [lead]);

  const utms = useMemo(() => {
    if (!lead) return {};
    return extractUTMs(`${lead.subject || ''}\n${lead.body_preview || ''}`);
  }, [lead]);

  if (!lead) return null;

  const utmEntries = Object.entries(utms);
  const sourceLabel = lead.source || 'inconnue';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lead-detail-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">{lead.subject || '(sans sujet)'}</DialogTitle>
          <DialogDescription className="text-xs flex flex-wrap items-center gap-2 mt-1">
            <span className="text-neutral-500">Reçu le</span>
            <strong className="text-neutral-700">{formatDateTime(lead.received_at)}</strong>
            <span className="text-neutral-300">·</span>
            <span className="text-neutral-500">de</span>
            <strong className="text-neutral-700 font-mono text-[11px] truncate max-w-[260px]">{lead.from_email}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Source + UTM badges */}
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant="outline" className="text-[10px]" data-testid="lead-source-badge">
              Source : {sourceLabel}
            </Badge>
            {utmEntries.length > 0 ? (
              utmEntries.map(([k, v]) => (
                <Badge
                  key={k}
                  className={`text-[10px] ${k === 'gclid' || k === 'fbclid' || k === 'msclkid'
                    ? 'bg-purple-100 text-purple-800 border-purple-200'
                    : 'bg-blue-100 text-blue-800 border-blue-200'} border`}
                  data-testid={`utm-${k}`}
                >
                  {k} : {v}
                </Badge>
              ))
            ) : (
              <span className="text-[10px] text-neutral-400 italic">
                Aucun UTM détecté · installez un plugin UTM Grabber sur le site pour enrichir les leads
              </span>
            )}
          </div>

          {/* Structured form fields — extracted from body */}
          {lead.extracted_fields && Object.keys(lead.extracted_fields).length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3" data-testid="lead-fields">
              <div className="flex items-center gap-1.5 text-xs font-medium text-blue-900 mb-2">
                <UserCircle className="w-3.5 h-3.5" />
                Informations du formulaire
              </div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                {LEAD_FIELD_ORDER.map(([key, label, isFull]) => {
                  const v = lead.extracted_fields[key];
                  if (!v) return null;
                  return (
                    <div key={key} className={isFull ? 'sm:col-span-2' : ''}>
                      <dt className="text-blue-700 font-medium uppercase tracking-wide text-[10px]">{label}</dt>
                      <dd className="text-neutral-900 mt-0.5 break-words">{v}</dd>
                    </div>
                  );
                })}
              </dl>
            </div>
          )}

          {/* Body preview */}
          {lead.body_preview ? (
            <div className="space-y-1.5">
              <Label className="text-xs">Contenu brut du formulaire</Label>
              <div
                className="bg-neutral-50 border border-neutral-200 rounded p-3 text-xs text-neutral-700 whitespace-pre-wrap font-mono leading-relaxed max-h-72 overflow-y-auto"
                data-testid="lead-body-preview"
              >
                {lead.body_preview}
              </div>
              <p className="text-[10px] text-neutral-400">Aperçu nettoyé du corps de l'email (texte uniquement, ~2000 caractères max).</p>
            </div>
          ) : (
            <p className="text-xs text-neutral-400 italic">Aucun aperçu de contenu disponible pour ce lead.</p>
          )}

          <hr className="border-neutral-200" />

          <div className="space-y-1.5">
            <Label className="text-xs">Client</Label>
            <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
              <SelectTrigger><SelectValue placeholder="Aucun client" /></SelectTrigger>
              <SelectContent>
                {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Statut</Label>
            <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.status === 'won' && (
            <div className="space-y-1.5">
              <Label className="text-xs">Valeur de la vente (€)</Label>
              <Input
                type="number"
                min={0}
                step={50}
                value={form.sale_value}
                onChange={(e) => setForm({ ...form, sale_value: parseFloat(e.target.value) || 0 })}
              />
            </div>
          )}
          {form.status === 'lost' && (
            <div className="space-y-1.5">
              <Label className="text-xs">Raison de la perte</Label>
              <Select value={form.loss_reason} onValueChange={(v) => setForm({ ...form, loss_reason: v })}>
                <SelectTrigger data-testid="admin-loss-reason"><SelectValue placeholder="Choisir une raison..." /></SelectTrigger>
                <SelectContent>
                  {LOSS_REASONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">Notes</Label>
            <Textarea
              rows={3}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Détails de la vente, contact, etc."
            />
          </div>

          {/* Bonus — Google Ads Offline Conversion upload */}
          {lead.status === 'won' && lead.gclid && lead.sale_value > 0 && (
            <GoogleAdsUploadBlock lead={lead} />
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button onClick={() => onSave(form)} className="bg-[#002FA7] hover:bg-[#002277]">
              Enregistrer
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GoogleAdsUploadBlock({ lead }) {
  const [uploading, setUploading] = useState(false);
  const [uploadedAt, setUploadedAt] = useState(lead.google_ads_uploaded_at);
  const [error, setError] = useState(lead.google_ads_upload_error || '');

  const handleUpload = async () => {
    setUploading(true);
    setError('');
    try {
      const { data } = await axios.post(
        `${API}/google-ads/upload-conversion/${lead.id}`,
        {},
        { withCredentials: true }
      );
      setUploadedAt(data.uploaded_at);
      toast.success('Conversion poussée à Google Ads ✓');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erreur upload';
      setError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  if (uploadedAt) {
    return (
      <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-800 flex items-center gap-2" data-testid="ga-uploaded">
        <CheckCircle2 className="w-4 h-4" />
        Conversion poussée à Google Ads le {new Date(uploadedAt).toLocaleString('fr-FR')}
      </div>
    );
  }

  return (
    <div className="rounded border border-blue-200 bg-blue-50 p-3 space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-blue-900">
          <strong>Boucler la boucle Smart Bidding</strong>
          <p className="text-[11px] mt-0.5">Pousser ce lead à Google Ads améliorera l'algorithme d'enchères pour les futures campagnes.</p>
        </div>
        <Button
          size="sm"
          onClick={handleUpload}
          disabled={uploading}
          className="bg-blue-600 hover:bg-blue-700 shrink-0"
          data-testid="ga-upload-btn"
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Pousser à Google Ads'}
        </Button>
      </div>
      {error && <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1">{error}</div>}
    </div>
  );
}
